import { useCallback, useRef } from "react";

interface ConfettiOptions {
  particleCount?: number;
  spread?: number;
  origin?: { x?: number; y?: number };
  colors?: string[];
}

export function useConfetti() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const instanceRef = useRef<unknown>(null);

  const initCanvas = useCallback(() => {
    if (canvasRef.current) return canvasRef.current;
    const canvas = document.createElement("canvas");
    canvas.style.position = "fixed";
    canvas.style.top = "0";
    canvas.style.left = "0";
    canvas.style.width = "100vw";
    canvas.style.height = "100vh";
    canvas.style.pointerEvents = "none";
    canvas.style.zIndex = "9999";
    document.body.appendChild(canvas);
    canvasRef.current = canvas;
    return canvas;
  }, []);

  const fire = useCallback(
    async (opts: ConfettiOptions = {}) => {
      const { default: confetti } = await import("canvas-confetti");
      const canvas = initCanvas();
      const customConfetti = confetti.create(canvas, { resize: true });
      instanceRef.current = customConfetti;

      customConfetti({
        particleCount: opts.particleCount ?? 120,
        spread: opts.spread ?? 70,
        origin: opts.origin ?? { x: 0.5, y: 0.6 },
        colors: opts.colors ?? ["#fbbf24", "#ec4899", "#8b5cf6", "#06b6d4", "#34d399"],
      });

      setTimeout(() => {
        customConfetti({
          particleCount: 60,
          spread: 100,
          origin: { x: 0.3, y: 0.5 },
          colors: ["#fbbf24", "#ec4899", "#8b5cf6"],
        });
        customConfetti({
          particleCount: 60,
          spread: 100,
          origin: { x: 0.7, y: 0.5 },
          colors: ["#06b6d4", "#34d399", "#fbbf24"],
        });
      }, 250);
    },
    [initCanvas],
  );

  const cleanup = useCallback(() => {
    if (canvasRef.current) {
      canvasRef.current.remove();
      canvasRef.current = null;
    }
    instanceRef.current = null;
  }, []);

  return { fire, cleanup };
}
